import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import Slider from './Slider';

const API_BASE_URL = 'https://ttspro-api.netlify.app/';

const TextToSpeech = () => {
    const [text, setText] = useState('');
    const [group, setGroup] = useState('en-US');
    const [voices, setVoices] = useState([]);
    const [selectedVoice, setSelectedVoice] = useState('');
    const [isConverting, setIsConverting] = useState(false);
    const [audioURL, setAudioURL] = useState('');

    const [groupOptions, setGroupOptions] = useState([]);

    
    const [speed, setSpeed] = useState(0);
    const [volume, setVolume] = useState(0);
    const [pitch, setPitch] = useState(0);



    useEffect(() => {
        const loadGroups = async () => {
        try {
            const res = await fetch(`${API_BASE_URL}?engine=edge&action=groups`);
            const result = await res.json();

            // ⚠️ body là STRING → phải JSON.parse
            const data = typeof result.body === 'string'
            ? JSON.parse(result.body)
            : result.body;

            setGroupOptions(data.options || []);

        } catch (err) {
            console.error('Load groups failed', err);
            setGroupOptions([]);
        }
        };

        loadGroups();
    }, []);


    useEffect(() => {
        if (audioURL) {
            playAudio();
        }
    }, [audioURL]);

    const playAudio = () => {
        const audioElement = document.getElementById('audio');
        if (audioElement) {
            audioElement.play();
        }
    };

    // useEffect to update voices when group changes
    useEffect(() => {
        const loadVoicesByGroup = async () => {
            try {
                const res = await fetch(
                `${API_BASE_URL}?engine=edge&action=voices-by-group&group=${encodeURIComponent(group)}`
                );

                const result = await res.json();

                // ⚠️ body là string → parse
                const data = typeof result.body === 'string'
                ? JSON.parse(result.body)
                : result.body;

                const options = data.options || [];

                setVoices(options);
                setSelectedVoice(options[0]?.value || '');
            } catch (err) {
                console.error('Load voices failed', err);
                setVoices([]);
                setSelectedVoice('');
            }
        };

        loadVoicesByGroup();
    }, [group]); // This effect runs when `group` changes

    // Handle group change
    const handleGroupChange = (selectedOption) => {
        setGroup(selectedOption.value);  // Update group based on selected option
    };

    // Handle voice selection change using react-select
    const handleVoiceChange = (selectedOption) => {
        setSelectedVoice(selectedOption.value); // Update selected voice
    };

    const handleTextChange = (event) => {
        setText(event.target.value);
    };

    const  handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            const fr = new FileReader();
            fr.onload = () => {
                setText(fr.result);
            };
            fr.readAsText(file);
        }
    };

    const handleRefresh = () => {
        setText('');
    };

    const handleCopy = () => {
        navigator.clipboard.writeText(text).then(() => {
            alert("Success! Copied successfully. Please paste anywhere.");
        }).catch(err => {
            console.error('Failed to copy: ', err);
        });
    };

    const handleDownload = () => {
        const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = 'text.txt';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const callEdgeTTS = async ({
    text,
    voice,
    pitch = '+0Hz',
    rate = '0%',
    volume = '100%',
    format = 'mp3'
    }) => {
    if (!text) return null;

    const res = await fetch(`${API_BASE_URL}?engine=edge`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            text,
            voice,
            pitch,
            rate,
            volume,
            format
        })
    })

    if (!res.ok) {
        throw new Error('Edge TTS request failed');
    }

    // ✅ ĐỌC RAW BINARY
    const buffer = await res.arrayBuffer();
    console.log(buffer)

    // ✅ MIME ĐÚNG
    const mime =
        format === 'mp3'
        ? 'audio/mpeg'
        : 'audio/webm; codecs=opus';

    // ✅ TẠO AUDIO BLOB
    const blob = new Blob([buffer], { type: mime });
    const url = URL.createObjectURL(blob);

    return {
        url,
        blob,
        size: res.headers.get('x-audio-size'),
        duration: res.headers.get('x-audio-duration')
    };
    };

    const splitTextIntoChunks = (text, maxLength = 900) => {
        const chunks = [];
        let current = '';

        const sentences = text.match(/[^.!?]+[.!?]*/g) || [text];

        for (const sentence of sentences) {
            if (!sentence.trim()) continue;

            // nếu 1 câu quá dài → cắt thô
            if (sentence.length > maxLength) {
                if (current.trim()) {
                    chunks.push(current.trim());
                    current = '';
                }

                for (let i = 0; i < sentence.length; i += maxLength) {
                    chunks.push(sentence.slice(i, i + maxLength).trim());
                }
                continue;
            }

            if ((current + sentence).length > maxLength) {
                chunks.push(current.trim());
                current = sentence;
            } else {
                current += sentence;
            }
        }

        if (current.trim()) chunks.push(current.trim());

        return chunks;
    };

    async function asyncPool(poolLimit, array, iteratorFn) {
        const ret = [];
        const executing = [];

        for (const [index, item] of array.entries()) {
            const p = Promise.resolve().then(() => iteratorFn(item, index));
            ret.push(p);

            if (poolLimit <= array.length) {
                const e = p.then(() => executing.splice(executing.indexOf(e), 1));
                executing.push(e);
                if (executing.length >= poolLimit) {
                    await Promise.race(executing);
                }
            }
        }

        return Promise.all(ret);
    }

    const processText = async () => {
        try {
            setAudioURL('');
            setIsConverting(true);

            const MAX_TEXT_LENGTH = 900;
            const CONCURRENCY = 3; // 👈 đổi thành 5 nếu server chịu được

            const textParts = splitTextIntoChunks(text, MAX_TEXT_LENGTH);
            if (!textParts.length) throw new Error('Text is empty after split');

            console.log(`🔹 Chunks: ${textParts.length}, concurrency: ${CONCURRENCY}`);

            const audioResults = await asyncPool(
                CONCURRENCY,
                textParts,
                (part, index) => {
                    console.log(`🎙️ Processing ${index + 1}/${textParts.length}`);
                    return callEdgeTTS({
                        text: part,
                        voice: selectedVoice,
                        rate: `${speed >= 0 ? "+" : ""}${speed}%`,
                        volume: `${volume >= 0 ? "+" : ""}${volume}%`,
                        pitch: `${pitch >= 0 ? "+" : ""}${pitch}Hz`,
                        format: 'mp3'
                    });
                }
            );

            const audioBlobs = audioResults.map((audio, i) => {
                if (!audio?.blob) {
                    throw new Error(`Missing blob at chunk ${i + 1}`);
                }
                return audio.blob;
            });

            const finalBlob = new Blob(audioBlobs, {
                type: audioBlobs[0].type || 'audio/mpeg'
            });

            setAudioURL(URL.createObjectURL(finalBlob));
            console.log('✅ Audio merged');

        } catch (err) {
            console.error('❌ processText error:', err);
            alert(err.message || 'Convert failed');
        } finally {
            setIsConverting(false);
        }
    };

    const handleConvertAudio = async () => {
        if (text.trim() === "") {
            alert("Please enter some text.");
            return;
        }
        await processText();
    };

    const handleDownloadAudio = async () => {
        if (audioURL.length === 0) {
            alert("Please convert before downloading.");
            return;
        }
        downloadAudio();
    };
    const downloadAudio = () => {
        const a = document.createElement('a');
        a.href = audioURL;
        a.download = 'audio.mp3'; // Set the desired file name
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    }
    

    return (
        <div className="my-3 card">
            <div className='row'>
                <div className='col-lg-6'>
                    <div className="card">
                        <div id="action-button-container">
                            <button className="btn btn-outline-primary custom-file-uploader" title="Import Text">
                                <input id="inputfile" type="file" onChange={handleFileChange} style={{ display: 'none' }} />
                                <span onClick={() => document.getElementById('inputfile').click()}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-folder-plus" viewBox="0 0 16 16">
                                        <path d="m.5 3 .04.87a1.99 1.99 0 0 0-.342 1.311l.637 7A2 2 0 0 0 2.826 14H9v-1H2.826a1 1 0 0 1-.995-.91l-.637-7A1 1 0 0 1 2.19 4h11.62a1 1 0 0 1 .996 1.09L14.54 8h1.005l.256-2.819A2 2 0 0 0 13.81 3H9.828a2 2 0 0 1-1.414-.586l-.828-.828A2 2 0 0 0 6.172 1H2.5a2 2 0 0 0-2 2zm5.672-1a1 1 0 0 1 .707.293L7.586 3H2.19c-.24 0-.47.042-.683.12L1.5 2.98a1 1 0 0 1 1-.98h3.672z" />
                                        <path d="M13.5 10a.5.5 0 0 1 .5.5V12h1.5a.5.5 0 1 1 0 1H14v1.5a.5.5 0 1 1-1 0V13h-1.5a.5.5 0 0 1 0-1H13v-1.5a.5.5 0 0 1 .5-.5z" />
                                    </svg>
                                </span>
                            </button>
                            <button className="btn btn-outline-primary btn-refresh" title="Refresh" onClick={handleRefresh}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-arrow-repeat" viewBox="0 0 16 16">
                                    <path d="M11.534 7h3.932a.25.25 0 0 1 .192.41l-1.966 2.36a.25.25 0 0 1-.384 0l-1.966-2.36a.25.25 0 0 1 .192-.41zm-11 2h3.932a.25.25 0 0 0 .192-.41L2.692 6.23a.25.25 0 0 0-.384 0L.342 8.59A.25.25 0 0 0 .534 9z" />
                                    <path fillRule="evenodd" d="M8 3c-1.552 0-2.94.707-3.857 1.818a.5.5 0 1 1-.771-.636A6.002 6.002 0 0 1 13.917 7H12.9A5.002 5.002 0 0 0 8 3zM3.1 9a5.002 5.002 0 0 0 8.757 2.182.5.5 0 1 1 .771.636A6.002 6.002 0 0 1 2.083 9H3.1z" />
                                </svg>
                            </button>
                            <button className="btn btn-outline-primary btn-copy" title="Copy" onClick={handleCopy}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-clipboard" viewBox="0 0 16 16">
                                    <path d="M4 1.5H3a2 2 0 0 0-2 2V14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V3.5a2 2 0 0 0-2-2h-1v1h1a1 1 0 0 1 1 1V14a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V3.5a1 1 0 0 1 1-1h1v-1z" />
                                    <path d="M9.5 1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-3a.5.5 0 0 1-.5-.5v-1a.5.5 0 0 1 .5-.5h3zm-3-1A1.5 1.5 0 0 0 5 1.5v1A1.5 1.5 0 0 0 6.5 4h3A1.5 1.5 0 0 0 11 2.5v-1A1.5 1.5 0 0 0 9.5 0h-3z" />
                                </svg>
                            </button>
                            <button className="btn btn-outline-primary btn-download" title="Download Text" onClick={handleDownload}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-save" viewBox="0 0 16 16">
                                    <path d="M2 1a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H9.5a1 1 0 0 0-1 1v7.293l2.646-2.647a.5.5 0 0 1 .708.708l-3.5 3.5a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L7.5 9.293V2a2 2 0 0 1 2-2H14a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V2a2 2 0 0 1 2-2h2.5a.5.5 0 0 1 0 1H2z" />
                                </svg>
                            </button>
                        </div>
                    </div>
                    <div style={{ position: "relative" }} className="form-group">
                        <textarea
                            style={{ minHeight: 400, paddingBottom: 30 }}
                            className="form-control"
                            id="texto"
                            name="texto"
                            placeholder="Paste Text..."
                            rows="10"
                            value={text}
                            onChange={handleTextChange}
                        />

                        {/* Character counter */}
                        <div
                            style={{
                            position: "absolute",
                            bottom: 8,
                            right: 20,
                            fontSize: 12,
                            color: "#666",
                            pointerEvents: "none"
                            }}
                        >
                            {text.length} characters
                        </div>
                    </div>
                </div>
                <div className='col-lg-6'>
                    <div className='m-2'>
                        <Slider label="Speed" value={speed} setValue={setSpeed} min={-100} max={100} unit="%" type="speed" />
                        <Slider label="Volume" value={volume} setValue={setVolume} min={-100} max={100} unit="%" type="volume" />
                        <Slider label="Pitch" value={pitch} setValue={setPitch} min={-100} max={100} unit="Hz" type="pitch" />
                        <div className="row">
                            <div className="col-lg-6">
                                <div className="mt-2">
                                    <Select
                                        menuPortalTarget={document.body} // Đặt menu dropdown ngoài body
                                        styles={{
                                            menuPortal: base => ({ ...base, zIndex: 9999 }) // Đảm bảo menu luôn trên các phần tử khác
                                        }}                                
                                        id="group"
                                        value={groupOptions.find(option => option.value === group)}  // Set selected value
                                        onChange={handleGroupChange}  // Handle group selection change
                                        options={groupOptions} // Provide options for the dropdown
                                        isSearchable // Enables search within the dropdown
                                        placeholder="Search and select a group"
                                    />
                                </div>
                            </div>
                            <div className="col-lg-6">
                                <div className="mt-2">
                                    <Select
                                        menuPortalTarget={document.body} // Đặt menu dropdown ngoài body
                                        styles={{
                                            menuPortal: base => ({ ...base, zIndex: 9999 }) // Đảm bảo menu luôn trên các phần tử khác
                                        }}                                
                                        id="voice"
                                        value={voices.find(option => option.value === selectedVoice)}  // Set selected voice
                                        onChange={handleVoiceChange} // Handle voice selection change
                                        options={voices} // Provide options for the dropdown
                                        isSearchable // Enables search within the dropdown
                                        placeholder="Search and select a voice"
                                    />
                                </div>
                            </div>
                        </div>
                        <div className='text-center my-3'>
                            <button className="btn btn-primary" onClick={handleConvertAudio}>
                                {isConverting ? "Converting..." : "Convert"}
                            </button>
                        </div>
                        <div className='row'>
                            <div className="col-lg-9">
                                <audio className="w-100" id="audio" controls src={audioURL} />
                            </div>
                            <div className="col-lg-3">
                                <button className="btn btn-outline-primary mt-2" onClick={handleDownloadAudio}>
                                    Download
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TextToSpeech;
